import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import OptimizedImage from '../components/OptimizedImage';
import CookieConsent from '../components/CookieConsent';
import SEOHead from '../components/SEOHead';
import { useTranslation } from '../hooks/useTranslation';
import { getCarouselImages, pexelsImages } from '../config/pexels-images';
import { getImagePath } from '../utils/imagePath';
import MobileStoresComingSoon from '../components/MobileStoresComingSoon';

const Landing = () => {
  console.log('🏠 Landing component rendering...');
  const { t } = useTranslation();
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [typedText, setTypedText] = useState('');
  const [showCursor, setShowCursor] = useState(true);
  
  const fullText = t('landing.title');
  console.log('🏠 Landing fullText:', fullText);

  // Images locales depuis assets/logo - identiques au site web
  // Utilise les images locales (1.webp à 5.webp) pour correspondre exactement au site
  const carouselImagesBase = getCarouselImages(false); // false = utiliser images locales (identiques au site web)
  const carouselImages = carouselImagesBase.map((img, index) => ({
    ...img,
    title: t(`landing.hero.carousel.image${index + 1}.title`),
    subtitle: t(`landing.hero.carousel.image${index + 1}.subtitle`)
  }));

  // Effet de frappe au clavier
  useEffect(() => {
    let currentIndex = 0;
    setTypedText('');
    
    const typingInterval = setInterval(() => {
      if (currentIndex < fullText.length) {
        setTypedText(fullText.substring(0, currentIndex + 1));
        currentIndex++;
      } else {
        clearInterval(typingInterval);
      }
    }, 100);
    
    return () => clearInterval(typingInterval);
  }, []);

  // Curseur clignotant
  useEffect(() => {
    const cursorInterval = setInterval(() => {
      setShowCursor(prev => !prev);
    }, 530);
    
    return () => clearInterval(cursorInterval);
  }, []);

  // Rotation automatique du carrousel
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageIndex((prev) => (prev + 1) % carouselImages.length);
    }, 5000); // Change toutes les 5 secondes

    return () => clearInterval(interval);
  }, [carouselImages.length]);
  const features = [
    {
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
        </svg>
      ),
      title: t('landing.features.dashboard.title'),
      description: t('landing.features.dashboard.description')
    },
    {
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
        </svg>
      ),
      title: t('landing.features.transactions.title'),
      description: t('landing.features.transactions.description')
    },
    {
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
        </svg>
      ),
      title: t('landing.features.positions.title'),
      description: t('landing.features.positions.description')
    },
    {
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
        </svg>
      ),
      title: t('landing.features.stock.title'),
      description: t('landing.features.stock.description')
    },
    {
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
      title: t('landing.features.margins.title'),
      description: t('landing.features.margins.description')
    },
    {
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
      title: t('landing.features.history.title'),
      description: t('landing.features.history.description')
    }
  ];

  console.log('🏠 Landing return statement reached');
  
  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white" style={{ minHeight: '100vh', backgroundColor: '#f5f7fa' }}>
      <SEOHead 
        title={t('landing.title')}
        description={t('landing.description')}
        keywords="gestion or, métaux précieux, ERP or, plateforme SaaS, Golden Mali, trading or, prix or temps réel, XAUUSD, gestion flux or"
        type="website"
      />
      {/* Header simplifié pour page publique */}
      <header className="bg-white shadow-md border-b border-gray-200" style={{ backgroundColor: 'white', padding: '16px' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center space-x-3">
              <OptimizedImage
                src={getImagePath("/assets/logo/GM.png")}
                alt="Golden Mali SARL"
                className="h-12 w-12 rounded-lg object-contain"
              />
              <div>
                <div className="text-xl font-bold text-navy-800">{t('landing.title')}</div>
                <div className="text-xs text-gray-500">{t('landing.subtitle')}</div>
              </div>
            </Link>
            <Link
              to="/login"
              className="px-6 py-2 bg-gradient-to-r from-gold-500 to-gold-600 text-white font-semibold rounded-lg hover:from-gold-600 hover:to-gold-700 transition-all shadow-md hover:shadow-lg"
            >
              {t('landing.hero.loginLink')}
            </Link>
          </div>
        </div>
      </header>

      {/* Carrousel d'images Hero */}
      <section className="relative h-96 md:h-[450px] lg:h-[550px] xl:h-[650px] 2xl:h-[750px] overflow-hidden">
        {/* Images du carrousel */}
        {carouselImages.map((image, index) => (
          <div
            key={index}
            className={`absolute inset-0 transition-opacity duration-1000 ${
              index === currentImageIndex ? 'opacity-100' : 'opacity-0'
            }`}
          >
            {/* Gradient de fallback */}
            <div
              className="absolute inset-0"
              style={{
                background: image.fallbackGradient,
                zIndex: 1
              }}
            />
            {/* Image - locale avec fallback Pexels */}
            <img
              src={image.url}
              alt={image.title}
              className="absolute inset-0 w-full h-full object-cover transition-opacity duration-300"
              style={{ zIndex: 2 }}
              loading="eager"
              onError={(e) => {
                // Si l'image locale ne charge pas, essayer Pexels en fallback
                if (image.url && !image.url.startsWith('http') && pexelsImages.carousel[index]?.url) {
                  e.target.src = pexelsImages.carousel[index].url;
                  return;
                }
                // Si aucune image ne charge, masquer et garder le gradient
                e.target.style.display = 'none';
                e.target.style.opacity = '0';
              }}
              onLoad={(e) => {
                // Si l'image charge correctement, l'afficher
                e.target.style.display = 'block';
                e.target.style.opacity = '1';
              }}
            />
            {/* Overlay sombre pour meilleure lisibilité */}
            <div className="absolute inset-0 bg-gradient-to-r from-navy-900/85 via-navy-800/75 to-navy-900/85" style={{ zIndex: 3 }}></div>
          </div>
        ))}
        
        {/* Contenu Hero superposé sur le carrousel */}
        <div className="absolute inset-0 flex items-center justify-center z-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
            <div className="text-center">
              <h1 className="text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-black mb-4 md:mb-6 text-white drop-shadow-2xl hover:text-gold-300 transition-colors duration-300 cursor-default">
                <span className="font-mono">{typedText}</span>
                <span className={`inline-block w-1 md:w-1.5 h-10 md:h-14 lg:h-16 xl:h-20 bg-gold-400 ml-2 align-middle ${showCursor ? 'opacity-100' : 'opacity-0'} transition-opacity duration-300 animate-pulse`}>|</span>
              </h1>
              <p className="text-lg md:text-xl lg:text-2xl xl:text-3xl font-semibold text-gold-300 mb-3 md:mb-4 drop-shadow-lg hover:text-gold-200 transition-colors duration-300">
                {t('landing.hero.subtitle')}
              </p>
              <p className="text-sm md:text-base lg:text-lg xl:text-xl text-gray-200 mb-8 md:mb-12 max-w-3xl mx-auto drop-shadow-md hover:text-white transition-colors duration-300">
                {t('landing.hero.description')}
              </p>
              
              {/* CTA Button */}
              <div className="flex justify-center">
                <Link
                  to="/login"
                  className="px-8 md:px-10 py-4 md:py-5 bg-gradient-to-r from-gold-500 to-gold-600 text-white text-lg md:text-xl font-black rounded-xl shadow-2xl hover:shadow-gold-500/50 hover:from-gold-600 hover:to-gold-700 transition-all duration-300 transform hover:scale-110 hover:-translate-y-1 active:scale-95"
                >
                  <span className="flex items-center justify-center gap-3">
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1" />
                    </svg>
                    {t('landing.hero.loginButton')}
                  </span>
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Indicateurs de pagination */}
        <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex gap-2 z-10">
          {carouselImages.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentImageIndex(index)}
              className={`h-2 rounded-full transition-all duration-300 ${
                index === currentImageIndex
                  ? 'w-8 bg-gold-500'
                  : 'w-2 bg-white/50 hover:bg-white/75'
              }`}
              aria-label={`Aller à l'image ${index + 1}`}
            />
          ))}
        </div>
      </section>

      {/* Séparateur décoratif professionnel */}
      <div className="relative py-8 md:py-12 bg-gradient-to-b from-navy-900 via-white to-white">
        <div className="w-full px-3 sm:px-4 md:px-6 lg:px-8 xl:px-12 2xl:px-16">
          <div className="relative">
            <div className="h-1 bg-gradient-to-r from-transparent via-gold-400 to-transparent"></div>
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="bg-white px-6 md:px-8">
                <div className="w-4 h-4 md:w-6 md:h-6 bg-gold-500 rounded-full shadow-xl"></div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Section Accès Requis */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-br from-gold-50 to-gold-100 rounded-2xl p-8 md:p-12 border-2 border-gold-300 shadow-2xl hover:shadow-gold-500/30 transition-all duration-300 transform hover:scale-[1.02] hover:-translate-y-1">
            <div className="text-center mb-8">
              <div className="inline-flex items-center justify-center w-20 h-20 bg-gold-500 rounded-full mb-6">
                <svg className="w-12 h-12 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
              </div>
              <h2 className="text-3xl md:text-4xl font-black text-navy-800 mb-4">
                {t('landing.restricted.title')}
              </h2>
              <p className="text-lg md:text-xl text-gray-700 leading-relaxed mb-6">
                {t('landing.restricted.description')}
                <br />
                <span className="font-bold text-navy-800">{t('landing.restricted.mustRequest')}</span>
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 mb-8 border-l-4 border-gold-500">
              <h3 className="text-xl font-bold text-navy-800 mb-4">{t('landing.restricted.howToGet')}</h3>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start gap-3">
                  <svg className="w-6 h-6 text-gold-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span>{t('landing.restricted.step1')}</span>
                </li>
                <li className="flex items-start gap-3">
                  <svg className="w-6 h-6 text-gold-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span>{t('landing.restricted.step2')}</span>
                </li>
                <li className="flex items-start gap-3">
                  <svg className="w-6 h-6 text-gold-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span>{t('landing.restricted.step3')}</span>
                </li>
                <li className="flex items-start gap-3">
                  <svg className="w-6 h-6 text-gold-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span>{t('landing.restricted.step4')}</span>
                </li>
              </ul>
            </div>

            <MobileStoresComingSoon />

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/login"
                className="px-8 py-4 bg-gradient-to-r from-gold-500 to-gold-600 text-white text-lg font-black rounded-xl shadow-2xl hover:shadow-gold-500/50 hover:from-gold-600 hover:to-gold-700 transition-all duration-300 transform hover:scale-105 active:scale-95 text-center"
              >
                <span className="flex items-center justify-center gap-3">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1" />
                  </svg>
                  {t('landing.restricted.alreadyHave')}
                </span>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Séparateur décoratif professionnel */}
      <div className="relative py-8 md:py-12 bg-gradient-to-b from-white via-gray-50 to-gray-50">
        <div className="w-full px-3 sm:px-4 md:px-6 lg:px-8 xl:px-12 2xl:px-16">
          <div className="relative">
            <div className="h-1 bg-gradient-to-r from-transparent via-gold-400 to-transparent"></div>
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="bg-gray-50 px-6 md:px-8">
                <div className="w-4 h-4 md:w-6 md:h-6 bg-gold-500 rounded-full shadow-xl"></div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Section Fonctionnalités */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-black text-navy-800 mb-4">
              {t('landing.features.title')}
            </h2>
            <div className="w-20 sm:w-24 h-0.5 sm:h-1 bg-gradient-to-r from-gold-500 to-gold-600 mx-auto mb-4"></div>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              {t('landing.features.subtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, index) => (
              <div
                key={index}
                className="group bg-white rounded-xl p-6 shadow-lg hover:shadow-2xl transition-all duration-500 border border-gray-200 hover:border-gold-300 relative overflow-hidden"
                style={{
                  transformStyle: 'preserve-3d',
                  transition: 'all 0.5s cubic-bezier(0.4, 0, 0.2, 1)'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'perspective(1000px) rotateX(2deg) rotateY(2deg) scale(1.05) translateY(-12px)';
                  e.currentTarget.style.boxShadow = '0 25px 50px -12px rgba(217, 119, 6, 0.25)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'perspective(1000px) rotateX(0deg) rotateY(0deg) scale(1) translateY(0)';
                  e.currentTarget.style.boxShadow = '';
                }}
              >
                {/* Effet de brillance au survol */}
                <div className="absolute inset-0 bg-gradient-to-br from-gold-500/0 via-gold-500/0 to-gold-500/0 group-hover:from-gold-500/5 group-hover:via-gold-500/10 group-hover:to-gold-500/5 transition-all duration-500 rounded-xl pointer-events-none"></div>
                
                <div className="relative z-10">
                  <div className="text-gold-600 mb-4 transform transition-all duration-500 group-hover:scale-110 group-hover:rotate-6 group-hover:text-gold-500">
                    {feature.icon}
                  </div>
                  <h3 className="text-xl font-bold text-navy-800 mb-2 group-hover:text-gold-600 transition-colors duration-300">
                    {feature.title}
                  </h3>
                  <div className="w-12 h-0.5 bg-gradient-to-r from-gold-500 to-gold-600 mb-3 group-hover:w-16 transition-all duration-300"></div>
                  <p className="text-gray-600 group-hover:text-gray-700 transition-colors duration-300">
                    {feature.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Séparateur décoratif professionnel */}
      <div className="relative py-8 md:py-12 bg-gradient-to-b from-gray-50 via-white to-white">
        <div className="w-full px-3 sm:px-4 md:px-6 lg:px-8 xl:px-12 2xl:px-16">
          <div className="relative">
            <div className="h-1 bg-gradient-to-r from-transparent via-gold-400 to-transparent"></div>
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="bg-white px-6 md:px-8">
                <div className="w-4 h-4 md:w-6 md:h-6 bg-gold-500 rounded-full shadow-xl"></div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Section Sécurité */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-navy-50 rounded-2xl p-8 md:p-12 border-l-4 border-gold-500 shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:scale-[1.02]">
            <h3 className="text-2xl md:text-3xl font-black text-navy-800 mb-6">
              {t('landing.security.title')}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="flex items-start gap-3">
                <svg className="w-6 h-6 text-gold-600 flex-shrink-0 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
                <div>
                  <h4 className="font-bold text-navy-800 mb-1">{t('landing.security.dataSecure.title')}</h4>
                  <p className="text-sm text-gray-600">{t('landing.security.dataSecure.description')}</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <svg className="w-6 h-6 text-gold-600 flex-shrink-0 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
                <div>
                  <h4 className="font-bold text-navy-800 mb-1">{t('landing.security.accessControlled.title')}</h4>
                  <p className="text-sm text-gray-600">{t('landing.security.accessControlled.description')}</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <svg className="w-6 h-6 text-gold-600 flex-shrink-0 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
                <div>
                  <h4 className="font-bold text-navy-800 mb-1">{t('landing.security.cloudSync.title')}</h4>
                  <p className="text-sm text-gray-600">{t('landing.security.cloudSync.description')}</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <svg className="w-6 h-6 text-gold-600 flex-shrink-0 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
                <div>
                  <h4 className="font-bold text-navy-800 mb-1">{t('landing.security.availability.title')}</h4>
                  <p className="text-sm text-gray-600">{t('landing.security.availability.description')}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <Footer />

      {/* Popup d'acceptation des cookies - Affiché systématiquement */}
      <CookieConsent />
    </div>
  );
};

export default Landing;
